import React from 'react';

import Link from 'next/link';
import Image from 'next/image';

import { resolveMarketingUrl } from '@/constants/routes.mapping';
import { FooterCertification, footerData } from '@/data/footerData';
import Logo, { LIPS_PARTNERED_LOGO_WHITE } from '@/components/ui/Logo';

interface FooterBottomProps {
  certifications: FooterCertification[];
}

const BADGE_HEIGHT_IN_PIXELS = 40;

const CertificationBadge = ({
  certification,
  className,
}: {
  certification: FooterCertification;
  className?: string;
}) => {
  if (!certification.imageUrl) return null;

  const { imageUrl, imageAlt, name, imageWidth, imageHeight } = certification;
  const height = imageHeight ?? BADGE_HEIGHT_IN_PIXELS;
  const width = imageWidth ?? BADGE_HEIGHT_IN_PIXELS * 2;

  return (
    <Image
      src={imageUrl}
      alt={imageAlt || name}
      width={width}
      height={height}
      unoptimized
      className={className ?? 'h-10 w-auto object-contain'}
      data-testid={`footer-certification-${certification.id}`}
    />
  );
};

export const FooterBottom: React.FC<FooterBottomProps> = ({
  certifications,
}) => {
  const logoData = footerData.logo;
  const copyrightText = footerData.copyrightText;
  const legalLinks = footerData.legalLinks || [];

  return (
    <div>
      <div className="flex flex-col items-center justify-center gap-8 md:flex-row md:justify-between md:gap-8">
        {/* Logo */}
        {logoData && (
          <div className="shrink-0">
            <Logo
              src={LIPS_PARTNERED_LOGO_WHITE}
              alt={logoData.alt || 'LIPS Healthcare'}
              href={resolveMarketingUrl(logoData.href) || logoData.href || '/'}
              width={373}
              height={112}
              className="h-28 w-auto object-contain"
            />
          </div>
        )}

        {/* Certifications */}
        {certifications.length > 0 && (
          <div className="flex items-center justify-center gap-2 md:items-center md:gap-6">
            {/* <span className="text-gray-300 text-sm font-medium md:hidden">
              Regulated by:
            </span> */}
            {certifications.map((cert) => (
              <div key={cert.id} className="flex items-center">
                {cert.href ? (
                  <Link
                    href={cert.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center transition-opacity hover:opacity-80"
                  >
                    <CertificationBadge
                      certification={cert}
                      className="h-6 w-auto object-contain md:h-10 md:w-auto"
                    />
                  </Link>
                ) : (
                  <CertificationBadge
                    certification={cert}
                    className="h-6 w-auto object-contain md:h-10 md:w-auto"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Copyright and Legal Links */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-6 gap-y-2">
        <p className="text-sm text-gray-300">{copyrightText}</p>
        {legalLinks.length > 0 && (
          <>
            {legalLinks.map((link) => (
              <Link
                key={link.id}
                href={resolveMarketingUrl(link.href) || link.href}
                className="text-sm text-gray-300 transition-colors hover:text-white"
              >
                {link.title}
              </Link>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
